import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { createContext, useMemo, useState, useContext, useCallback, useEffect, useRef } from "react";
import { useCouponCode } from "../talons/CartPage/PriceAdjustments/useCouponCode";
import { useUserContext } from "./user";
import { useCartContext } from "./cart";
import { useCatalogContext } from "@magento/peregrine/lib/context/catalog";
import { useCheckoutContext } from "@magento/peregrine/lib/context/checkout";
import { castToCorrectType } from "@Src_main/utils/generalUtils";
import { isCouponCodeAvailableForStore, isOfferAvailableForStore } from "@Src_main/utils/offerStoreAvailability";
const STORE_COUPON_VISIBILITY_RULES = [{
  couponBarcodePrefix: "125551208",
  allowedStoreCodes: ["mZgsNJJ3vZ"]
}];
const getCouponVisibilityRule = coupon => {
  const couponBarcode = String((coupon === null || coupon === void 0 ? void 0 : coupon.barcode) || "");
  return STORE_COUPON_VISIBILITY_RULES.find(rule => couponBarcode.startsWith(rule.couponBarcodePrefix));
};
const isCouponVisibleForStore = (coupon, storeCode) => {
  const visibilityRule = getCouponVisibilityRule(coupon);
  if (!visibilityRule) {
    return true;
  }
  if (!storeCode) {
    return false;
  }
  return visibilityRule.allowedStoreCodes.includes(String(storeCode));
};
export const CouponContext = createContext();
export const useCouponContext = () => useContext(CouponContext);
export const CouponProvider = _ref => {
  var _tableData$store, _tableData$store2, _tableData$store3, _currentUser$lsAccoun;
  let {
    children
  } = _ref;
  const [selectedCoupon, setSelectedCoupon] = useState(null);
  const [modalReminderSku, setModalReminderSku] = useState(null);
  const [couponAppliedsuccessfully, setCouponAppliedSuccessfully] = useState(false);
  const [closeTriggerCouponModal, setCloseTriggerCouponModal] = useState(null);
  const [publishedOffers, setPublishedOffers] = useState([]);
  const [couponLoaded, setCouponLoaded] = useState(false);
  const hydratedCustomerEmailRef = useRef(null);
  const refreshedCartIdRef = useRef(null);
  const {
    applyingCoupon,
    appliedCoupon,
    errors,
    handleApplyCoupon: applyCouponToCart,
    handleRemoveCoupon,
    removingCoupon,
    isLoading,
    getAvailableCoupon
  } = useCouponCode();
  const [{
    currentUser
  }] = useUserContext();
  const [{
    cartId
  }] = useCartContext();
  const [{
    products
  }] = useCatalogContext();
  const [{
    tableData
  }] = useCheckoutContext();
  const currentStoreCode = (tableData === null || tableData === void 0 ? void 0 : (_tableData$store = tableData.store) === null || _tableData$store === void 0 ? void 0 : _tableData$store.cps) || (tableData === null || tableData === void 0 ? void 0 : tableData.cps) || null;
  const couponsDisabled = Boolean(tableData === null || tableData === void 0 ? void 0 : (_tableData$store2 = tableData.store) === null || _tableData$store2 === void 0 ? void 0 : _tableData$store2.disableCoupon);
  const storeCode = tableData === null || tableData === void 0 ? void 0 : (_tableData$store3 = tableData.store) === null || _tableData$store3 === void 0 ? void 0 : _tableData$store3.ls_store_id;
  const currentUserCoupons = currentUser === null || currentUser === void 0 ? void 0 : (_currentUser$lsAccoun = currentUser.lsAccount) === null || _currentUser$lsAccoun === void 0 ? void 0 : _currentUser$lsAccoun.coupons;
  const currentUserEmail = currentUser === null || currentUser === void 0 ? void 0 : currentUser.email;
  const setUserCouponWrapper = useCallback(coupons => {
    const couponsParsed = (coupons || []).map(coupon => {
      const couponTemp = _objectSpread({}, coupon);
      delete couponTemp.additional_data;
      const additionalDataParsed = {};
      const additionalDataValues = Object.values(coupon.additional_data || {});
      additionalDataValues.forEach(data => {
        if (typeof (data === null || data === void 0 ? void 0 : data.key) !== "string") {
          return;
        }
        if (["MOD_CONDIMENTO", "MOD_TOPPING", "MOD_GRATTU"].includes(data.key)) {
          const parsedValue = typeof data.value === "string" ? data.value.split(",") : data.value;
          additionalDataParsed[data.key] = parsedValue;
        } else {
          const parsedValue = typeof data.value === "string" ? castToCorrectType(data.value, data.key) : data.value;
          additionalDataParsed[data.key] = parsedValue;
        }
      });
      return _objectSpread(_objectSpread({}, couponTemp), additionalDataParsed);
    });
    setPublishedOffers(couponsParsed);
  }, []);
  const userCoupons = useMemo(() => {
    if (couponsDisabled) {
      return [];
    }
    return publishedOffers.filter(offer => isCouponVisibleForStore(offer, currentStoreCode) && isOfferAvailableForStore(offer, storeCode));
  }, [couponsDisabled, currentStoreCode, publishedOffers, storeCode]);
  const handleApplyCoupon = useCallback((couponInput, onSuccess, onError) => {
    const couponCode = typeof couponInput === "string" ? couponInput : couponInput === null || couponInput === void 0 ? void 0 : couponInput.couponCode;
    const matchingOffer = publishedOffers.find(offer => offer.barcode === couponCode);
    const canApply = couponLoaded && !couponsDisabled && (!matchingOffer || isCouponVisibleForStore(matchingOffer, currentStoreCode)) && isCouponCodeAvailableForStore(publishedOffers, couponCode, storeCode);
    if (!canApply) {
      const error = new Error("This coupon is not available for the current store");
      console.warn("Coupon application blocked by Website availability rules", {
        couponCode,
        storeCode
      });
      if (onError) onError(error);
      return Promise.resolve(false);
    }
    return applyCouponToCart(couponInput, onSuccess, onError);
  }, [applyCouponToCart, couponLoaded, couponsDisabled, currentStoreCode, publishedOffers, storeCode]);
  const updateAvailableCoupon = useCallback(() => {
    getAvailableCoupon({
      onCompleted: data => {
        var _data$customer, _data$customer$lsAcco;
        if (data !== null && data !== void 0 && (_data$customer = data.customer) !== null && _data$customer !== void 0 && (_data$customer$lsAcco = _data$customer.lsAccount) !== null && _data$customer$lsAcco !== void 0 && _data$customer$lsAcco.coupons) {
          setUserCouponWrapper(data.customer.lsAccount.coupons);
        }
        setCouponLoaded(true);
      },
      onError: () => {
        setCouponLoaded(true);
      }
    });
  }, [getAvailableCoupon, setUserCouponWrapper]);
  useEffect(() => {
    if (couponsDisabled) {
      if (appliedCoupon) {
        handleRemoveCoupon();
      }
    }
  }, [appliedCoupon, couponsDisabled, handleRemoveCoupon]);
  useEffect(() => {
    if (couponsDisabled) {
      setCouponLoaded(true);
      return;
    }
    if (!currentUserEmail) {
      hydratedCustomerEmailRef.current = null;
      refreshedCartIdRef.current = null;
      setPublishedOffers([]);
      setCouponLoaded(true);
      return;
    }
    if (hydratedCustomerEmailRef.current !== currentUserEmail) {
      if (!Array.isArray(currentUserCoupons)) {
        setPublishedOffers([]);
        setCouponLoaded(false);
        return;
      }
      hydratedCustomerEmailRef.current = currentUserEmail;
      refreshedCartIdRef.current = cartId;
      setUserCouponWrapper(currentUserCoupons);
      setCouponLoaded(true);
      return;
    }
    if (cartId && refreshedCartIdRef.current !== cartId) {
      refreshedCartIdRef.current = cartId;
      updateAvailableCoupon();
    }
  }, [cartId, couponsDisabled, currentUserCoupons, currentUserEmail, setUserCouponWrapper, updateAvailableCoupon]);
  useEffect(() => {
    if (!appliedCoupon) {
      return;
    }
    const appliedOffer = publishedOffers.find(offer => offer.barcode === appliedCoupon);
    if (appliedOffer && (!isCouponVisibleForStore(appliedOffer, currentStoreCode) || !isOfferAvailableForStore(appliedOffer, storeCode))) {
      handleRemoveCoupon(appliedCoupon);
    }
  }, [appliedCoupon, currentStoreCode, handleRemoveCoupon, publishedOffers, storeCode]);
  const isCouponAvailableForSku = useCallback(sku => {
    if (!userCoupons || userCoupons.length === 0) {
      return false;
    }
    const now = new Date();
    const currentHour = now.getHours();
    const currentMinute = now.getMinutes();
    const currentTimeInMinutes = currentHour * 60 + currentMinute;
    return userCoupons.some(coupon => {
      // Check if SKU matches
      const skuMatches = coupon.items && coupon.items.includes(sku);
      if (!skuMatches) {
        return false;
      }

      // Check time validity
      let isWithinValidTime = true; // Default to true if no valid_time
      if (coupon !== null && coupon !== void 0 && coupon.valid_time) {
        try {
          const [startTimeStr, endTimeStr] = coupon.valid_time.split('-');
          const [startHour, startMinute] = startTimeStr.split(':').map(Number);
          const [endHour, endMinute] = endTimeStr.split(':').map(Number);
          const startTimeInMinutes = startHour * 60 + startMinute;
          const endTimeInMinutes = endHour * 60 + endMinute;

          // Handle overnight validity (e.g., 22:00 - 02:00)
          if (endTimeInMinutes < startTimeInMinutes) {
            isWithinValidTime = currentTimeInMinutes >= startTimeInMinutes || currentTimeInMinutes <= endTimeInMinutes;
          } else {
            isWithinValidTime = currentTimeInMinutes >= startTimeInMinutes && currentTimeInMinutes <= endTimeInMinutes;
          }
        } catch (error) {
          console.error("Error parsing coupon valid_time:", coupon.valid_time, error);
          isWithinValidTime = true; // Treat as valid if parsing fails
        }
      }
      return isWithinValidTime; // Return true only if SKU matches AND time is valid
    });
  }, [userCoupons]);
  const getSkusByBarcode = useCallback(coupon => {
    const couponSku = products.length ? products.find(x => coupon.items.includes(x.sku)) : null;
    return couponSku === null || couponSku === void 0 ? void 0 : couponSku.sku;
  }, [products]);
  const contextValue = {
    applyingCoupon,
    errors,
    handleApplyCoupon,
    handleRemoveCoupon,
    removingCoupon,
    isLoading,
    couponLoaded,
    userCoupons,
    selectedCoupon,
    setSelectedCoupon,
    isCouponAvailableForSku,
    appliedCoupon,
    getSkusByBarcode,
    modalReminderSku,
    setModalReminderSku,
    couponAppliedsuccessfully,
    setCouponAppliedSuccessfully,
    closeTriggerCouponModal,
    setCloseTriggerCouponModal
  };
  return /*#__PURE__*/React.createElement(CouponContext.Provider, {
    value: contextValue
  }, children);
};